import { useState } from 'react';
import type { OutputOf } from '@going-once/client';
import { api } from '../lib/api';
import { describeError, useAsync } from '../lib/useAsync';
import { Card, Pressable, Select, Stack, Text } from '../ui/primitives';
import { ErrorNote, Loading } from '../ui/state';
import { formatCents } from './Feed';
import { formatRemaining } from './Auction';

type Listing = OutputOf<'catalog.listing.list'>['listings'][number];

/**
 * The seller's side of a timed auction: open one, and see the ones that are running.
 *
 * **The only thing this screen chooses is when it ends.** Start price, reserve and
 * buy-it-now came from the listing when the seller published it, and `auctions.auction.
 * schedule` reads them from there — asking again here would let the auction disagree with
 * the listing a buyer is looking at.
 *
 * A listing already at auction shows as held rather than disappearing: "where did my
 * listing go" is the support ticket that state exists to prevent.
 */
export function SellerAuctions({ sellerId, onOpenAuction }: { sellerId: string; onOpenAuction: (id: string) => void }) {
  const listings = useAsync(() => api().query('catalog.listing.list', { limit: 25 }), []);
  const running = useAsync(
    () => api().query('auctions.auction.browse', { sellerRef: sellerId, limit: 25 }),
    [sellerId],
  );

  function reload() {
    listings.reload();
    running.reload();
  }

  return (
    <Stack gap="lg">
      <Text as="h2" step="heading">
        Timed auctions
      </Text>

      {running.error ? <ErrorNote error={running.error} onRetry={running.reload} /> : null}

      {running.data && running.data.auctions.length > 0 ? (
        <Stack gap="sm">
          {running.data.auctions.map((auction) => (
            <Card key={auction.auctionId} padding="sm">
              <Stack direction="row" justify="between" align="center" wrap gap="sm">
                <Stack gap="xs">
                  <Text>{auction.title}</Text>
                  <Text step="label" tone="fg.muted">
                    {formatCents(auction.currentPriceCents)} · {auction.liveBidCount} bids · ends in{' '}
                    {formatRemaining(auction.endTimeMs - auction.serverNowMs)}
                  </Text>
                </Stack>
                <Stack direction="row" gap="sm">
                  <Pressable variant="quiet" onPress={() => onOpenAuction(auction.auctionId)}>
                    View
                  </Pressable>
                  {auction.liveBidCount === 0 ? (
                    <CancelAuction auctionId={auction.auctionId} onCancelled={reload} />
                  ) : null}
                </Stack>
              </Stack>
            </Card>
          ))}
        </Stack>
      ) : null}

      {listings.loading ? <Loading what="your listings" /> : null}
      {listings.error ? <ErrorNote error={listings.error} onRetry={listings.reload} /> : null}

      {(() => {
        /**
         * The listings that already have an auction, **from the auction list above** —
         * not from the listing's own status.
         *
         * They are two records in two stores saying the same thing, and they can
         * disagree: repairing a stuck listing by hand sets one and not the other, and
         * then this panel offered "Start auction" on an item whose auction was running
         * three rows further up. The auction book is the authority on what is at
         * auction, so it is the one this screen asks.
         */
        const held = new Set((running.data?.auctions ?? []).map((auction) => auction.listingRef));

        const auctions = (listings.data?.listings ?? []).filter((listing) => listing.saleModes.includes('auction'));
        const ready = auctions.filter(
          (listing) => (listing.status === 'active' || listing.status === 'at_auction') && !held.has(listing.listingId),
        );
        // **Drafts are named, not filtered out.** A listing whose publish failed on a
        // missing attribute is the single most likely reason this panel looks empty, and
        // silently omitting it makes a fixable form error look like a missing feature.
        const drafts = auctions.filter((listing) => listing.status === 'draft');

        return (
          <>
            {listings.data && ready.length === 0 && drafts.length === 0 ? (
              <Text tone="fg.muted">
                Nothing to auction yet. Publish a listing above — everything listed here goes to auction, and it needs a
                starting price.
              </Text>
            ) : null}

            {ready.map((listing) => (
              <ListingRow key={listing.listingId} listing={listing} onScheduled={reload} />
            ))}

            {drafts.length > 0 ? (
              <Text tone="fg.muted">
                {drafts.length} draft{drafts.length === 1 ? '' : 's'} not published yet — a draft cannot go to auction.
                Finish it above; publish will say what it is missing.
              </Text>
            ) : null}
          </>
        );
      })()}
    </Stack>
  );
}

function ListingRow({ listing, onScheduled }: { listing: Listing; onScheduled: () => void }) {
  const [durationMinutes, setDurationMinutes] = useState('1440');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; code?: string; requestId?: string }>();

  const held = listing.status === 'at_auction';

  return (
    <Card padding="sm">
      <Stack gap="sm">
        <Stack direction="row" justify="between" align="center" wrap gap="sm">
          <Stack gap="xs">
            <Text>{listing.title}</Text>
            <Text step="label" tone="fg.muted">
              Starts at {formatCents(listing.startPriceCents ?? 0)}
              {listing.reserveCents ? ` · reserve ${formatCents(listing.reserveCents)}` : ''}
              {listing.binCents ? ` · buy now ${formatCents(listing.binCents)}` : ''}
            </Text>
          </Stack>
          {held ? (
            <Text step="label" tone="success">
              At auction
            </Text>
          ) : null}
        </Stack>

        {held ? null : (
          <Stack direction="row" gap="sm" align="end" wrap>
            <Select
              id={`duration-${listing.listingId}`}
              label="Runs for"
              value={durationMinutes}
              onChange={setDurationMinutes}
              options={[
                { value: '15', label: '15 minutes' },
                { value: '60', label: '1 hour' },
                { value: '1440', label: '1 day' },
                { value: '4320', label: '3 days' },
                { value: '10080', label: '7 days' },
              ]}
            />
            <Pressable
              variant="primary"
              busy={busy}
              onPress={() => {
                setBusy(true);
                setError(undefined);
                api()
                  .command(
                    'auctions.auction.schedule',
                    { listingId: listing.listingId, durationMinutes: Number(durationMinutes) },
                    {},
                  )
                  .then(onScheduled)
                  .catch((caught: unknown) => setError(describeError(caught)))
                  .finally(() => setBusy(false));
              }}
            >
              Start auction
            </Pressable>
          </Stack>
        )}

        {error ? <ErrorNote error={error} /> : null}
      </Stack>
    </Card>
  );
}

/** Cancelling is only offered with no bids, because the server refuses it after one. */
function CancelAuction({ auctionId, onCancelled }: { auctionId: string; onCancelled: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; code?: string; requestId?: string }>();
  return (
    <Stack gap="xs">
      <Pressable
        variant="quiet"
        busy={busy}
        onPress={() => {
          setBusy(true);
          api()
            .command('auctions.auction.cancel', { auctionId }, {})
            .then(onCancelled)
            .catch((caught: unknown) => setError(describeError(caught)))
            .finally(() => setBusy(false));
        }}
      >
        Cancel
      </Pressable>
      {error ? <ErrorNote error={error} /> : null}
    </Stack>
  );
}
